import { ApolloClient, ApolloLink, split } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import WebSocketLink from './WebSocketLink'
import { InMemoryCache } from '@apollo/client/cache'
import { getMainDefinition } from '@apollo/client/utilities'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'

import logout from '@/helpers/logout'
import { toast } from '@/ui'
import customFetch from '@/helpers/custom-fetch'
import config from '@/config'
import { logError } from '@/helpers/log-error'

const cache = new InMemoryCache({
  dataIdFromObject: ({ __typename, id, ...rest }: any) => {
    switch (__typename) {
      case 'Table':
        return rest.tableName
      default:
        return id
    }
  },
})

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors instanceof Array) {
    if (graphQLErrors[0].name === 'AuthorizationError') {
      logError(new Error(graphQLErrors?.[0]?.stack))

      toast.error(
        window.i18n.t('You do not have permission to perform this action'),
      )
      return
    }
    // clear session if tokens are invalid
    if (graphQLErrors[0].message === 'invalid signature') {
      logout()
      return
    }
  }
})

const authLink = setContext((_, { headers }) => {
  if (headers && (headers.authorization || headers.Authorization)) {
    return {
      headers,
      timezoneOffset: new Date().getTimezoneOffset(),
    }
  }

  const token = sessionStorage.getItem('accessToken')

  // Only add Authorization part if there is a token,
  // otherwise some jwt-less mutations/queries won't work
  const newHeaders = token
    ? {
        ...headers,
        authorization: `Bearer ${token}`,
      }
    : {
        ...headers,
      }

  return {
    headers: {
      ...newHeaders,
      timezoneOffset: new Date().getTimezoneOffset(),
    },
  }
})

const batchLink = new BatchHttpLink({
  uri: `${config.endpoints.graphql}/graphql`,
  fetch: customFetch,
  batchInterval: 50,
})

const wsLink = new WebSocketLink({
  url: `${config.endpoints.graphqlws}/graphql`,
  connectionParams: () => {
    return {
      authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
    }
  },
})

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  ApolloLink.from([
    errorLink,
    authLink,
    createUploadLink({
      uri: `${config.endpoints.graphql}/graphql`,
      fetch: customFetch,
    }),
  ]),
)

export const client = new ApolloClient({
  link,
  cache,
})

export const batchClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, batchLink]),
  cache,
})
